import React, { ReactElement, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import BlankSlate from '../../blocks/BlankSlate';
import SkeletonLoader from '../../../atoms/SkeletonLoader';
import Button from '../../../atoms/buttons/Button';
import OfferProfileGridView from './OfferProfileGridView';
import OfferProfileListView from './OfferProfileListView';
import TrackLoadingToMixpanel from '../../common/TrackLoadingToMixPanel';
import { RootState } from '../../../store';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { resetUnreadOneTapOffersCount } from '../../../actions/unreadOneTapOffersCountActions';
import { renderAvatar } from 'common';
import OfferAcceptUnlock from './OfferAcceptUnlock';
import { showAcceptUnlockModalAction } from 'actions/offerActions';

interface OfferListProps {
  activeRoute: any;
  noResultsTitle: any;
  noResultsMsg: any;
  noResultsImg: any;
  viewType: any;
  pageTitle: any;
  offerType: string;
}

const OfferList = ({
  activeRoute,
  noResultsTitle,
  noResultsMsg,
  noResultsImg,
  viewType,
  pageTitle,
  offerType,
}: OfferListProps): ReactElement => {
  const location = useLocation();
  const dispatch = useDispatch();

  const offerList = useSelector((state: RootState) => state.offers[offerType]);
  const isFetching = useSelector((state: RootState) => state.offers.isFetching);
  const count = useSelector((state: RootState) => state.unreadOneTapOffersCount.count);

  const { showOfferAcceptUnlockModal } = useSelector((state: RootState) => state.offers);
  const user = useSelector((state: RootState) => state.profile);

  useEffect(() => {
    if (location.pathname === '/offers/new' && viewType === 'list' && count > 0) {
      dispatch(resetUnreadOneTapOffersCount());
    }
  }, [location.pathname, viewType, count, dispatch]);

  const renderOfferItem = (item, key) => {
    const member = item.other_account.data;
    const offer = { data: item };
    const isFavoritedMe = item.favorited_me;
    const isMatched = member?.is_favorite?.favorited_me;

    return viewType === 'list' ? (
      <OfferProfileListView
        key={key}
        offerKey={key}
        profile={member}
        isFavoritedMe={isFavoritedMe}
        isMatched={isMatched}
        offer={offer}
        title={member.username}
        hashId={member.hash_id}
        timestamp={typeof item.created_at === 'string' ? item.created_at : item.created_at.date}
        pageTitle={pageTitle}
      />
    ) : (
      <OfferProfileGridView
        activeRoute={activeRoute}
        key={key}
        offerKey={key}
        profile={member}
        isFavoritedMe={isFavoritedMe}
        isMatched={isMatched}
        offer={offer}
        hashId={member.hash_id}
        pageTitle={pageTitle}
      />
    );
  };

  const handleCloseOfferAcceptUnlock = () => {
    dispatch(
      showAcceptUnlockModalAction({
        isOpen: false,
        amount: '',
        creditCost: null,
        username: '',
        hashId: '',
        urls: null,
        forceTrigger: Date.now(),
      })
    );
  };

  return (
    <>
      {isEmpty(offerList) && isFetching && (
        <>
          <TrackLoadingToMixpanel page={`offers - ${offerType}`} />
          <SkeletonLoader width="100%" />
        </>
      )}

      {!isFetching && isEmpty(offerList) && (
        <BlankSlate
          img={noResultsImg}
          title={noResultsTitle}
          message={noResultsMsg}
          action={
            <Button buttonType="primaryRed" to="/search">
              Find a date
            </Button>
          }
        />
      )}

      {!isEmpty(offerList) && offerList.map(renderOfferItem)}

      <OfferAcceptUnlock
        isModalOpen={showOfferAcceptUnlockModal.isOpen}
        offerAmount={showOfferAcceptUnlockModal.offerAmount}
        avatarSrc={showOfferAcceptUnlockModal?.urls && renderAvatar(showOfferAcceptUnlockModal.urls, true)}
        username={showOfferAcceptUnlockModal.username}
        successOpen={handleCloseOfferAcceptUnlock}
        buttonID="interestedOffers"
        currentUserIsGenerous={showOfferAcceptUnlockModal?.isGenerous}
        creditCost={showOfferAcceptUnlockModal.creditCost}
        hashId={showOfferAcceptUnlockModal.hashId}
        closeModal={handleCloseOfferAcceptUnlock}
        currentCredit={user.credits}
        threadUid={showOfferAcceptUnlockModal.threadUid}
      />
    </>
  );
};

export default OfferList;
